import React, { useState, useMemo, useEffect } from 'react';
import {
  Search, Clock, Play, ChevronRight, ChevronDown,
  Snowflake, Zap, Check, Package, X, Pause, QrCode, AlertTriangle,
} from 'lucide-react';

/**
 * OperacoesScreen — Tab "Operações" da BottomTabBar
 * Hierarchical: OFs → Lotes → Operações
 * Estilo baseado no TSX OperationsLayout (header branco, pills com scroll)
 * Drag and Drop para reordenar operações dentro de um lote
 */
export default function OperacoesScreen({
  ofsNovas = [],
  ofsIniciadas = [],
  ofsTerminadas = [],
  operacoesCongeladas = [],
  producaoAtiva = null,
  onSelectOf,
  onScanQR,
  onResumeCongelada,
  tarefasPontuais = [],
  tarefasHistoricoDia = [],
  frozenTarefaId = null,
  onOpenTarefas,
  onIniciarTarefa,
  onEditarTarefaConcluida,
  onStartOperation,
  onOpenActiveProduction,
  initialTab,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('todas');
  const [expandedOf, setExpandedOf] = useState(null);
  const [expandedLote, setExpandedLote] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab || 'operacoes');
  const [pontuaisSubTab, setPontuaisSubTab] = useState('disponiveis');

  // Reagir a mudanças no initialTab (e.g. quando clicam no badge de pontuais)
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  const allOFs = useMemo(() => {
    const novas = ofsNovas.map(o => ({ ...o, _status: 'nova' }));
    const iniciadas = ofsIniciadas.map(o => ({ ...o, _status: 'em_producao' }));
    const terminadas = ofsTerminadas.map(o => ({ ...o, _status: 'terminada' }));
    return [...iniciadas, ...novas, ...terminadas];
  }, [ofsNovas, ofsIniciadas, ofsTerminadas]);

  // Detect paused OFs (have congeladas)
  const ofsPausadas = useMemo(() => {
    return allOFs.filter(of =>
      operacoesCongeladas.some(c => c.of_id === of.id)
    );
  }, [allOFs, operacoesCongeladas]);

  const filtered = useMemo(() => {
    let list = allOFs;
    if (activeFilter === 'novas') list = list.filter(o => o._status === 'nova');
    if (activeFilter === 'em_producao') list = list.filter(o => o._status === 'em_producao');
    if (activeFilter === 'terminadas') list = list.filter(o => o._status === 'terminada');
    if (activeFilter === 'pausadas') {
      const pausadaIds = new Set(ofsPausadas.map(o => o.id));
      list = list.filter(o => pausadaIds.has(o.id));
    }
    if (searchQuery.length >= 2) {
      const q = searchQuery.toLowerCase();
      list = list.filter(o =>
        o.numero_of?.toLowerCase().includes(q) ||
        o.cliente?.toLowerCase().includes(q) ||
        o.artigo?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [allOFs, activeFilter, searchQuery, ofsPausadas]);

  const filters = [
    { id: 'todas', label: 'Todas', count: allOFs.length },
    { id: 'novas', label: 'Novas', count: ofsNovas.length },
    { id: 'em_producao', label: 'Em Produção', count: ofsIniciadas.length },
    { id: 'pausadas', label: 'Pausadas', count: ofsPausadas.length },
    { id: 'terminadas', label: 'Terminadas', count: ofsTerminadas.length },
  ];

  const toggleOf = (ofId, ofsArray) => {
    if (expandedOf === ofId) {
      setExpandedOf(null);
      setExpandedLote(null);
    } else {
      setExpandedOf(ofId);
      // Auto-expandir o primeiro lote disponível
      const of = (ofsArray || allOFs).find(o => o.id === ofId);
      const lotes = of?.lotes || [];
      const firstIncompleteLote = lotes.find(l => !isLoteComplete(l));
      setExpandedLote(firstIncompleteLote?.id || lotes[0]?.id || null);
    }
  };

  const toggleLote = (loteId) => {
    setExpandedLote(expandedLote === loteId ? null : loteId);
  };

  const isLoteComplete = (lote) => {
    const ops = lote.operacoes_operador || [];
    if (ops.length === 0) return false;
    return ops.every(op => {
      const max = op.quantidade_total || lote.quantidade || 1;
      const produzido = op.quantidade_produzida_todos ?? op.quantidade_produzida ?? 0;
      return produzido >= max;
    });
  };

  const getLoteProgress = (lote) => {
    const ops = lote.operacoes_operador || [];
    if (ops.length === 0) return { done: 0, total: 0, percent: 0 };
    const done = ops.filter(op => {
      const max = op.quantidade_total || lote.quantidade || 1;
      const produzido = op.quantidade_produzida_todos ?? op.quantidade_produzida ?? 0;
      return produzido >= max;
    }).length;
    return { done, total: ops.length, percent: Math.round((done / ops.length) * 100) };
  };

  return (
    <div className="flex flex-col h-screen pb-20" style={{ background: '#F8FAFC' }}>
      {/* Header — branco conforme TSX */}
      <div className="px-5 pt-5 pb-4" style={{ background: '#FFFFFF' }}>
        <div className="flex items-center justify-between mb-4">
          <h1
            className="font-black"
            style={{ fontSize: '28px', color: '#1E293B' }}
            data-testid="operacoes-title"
          >
            Operações
          </h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onScanQR && onScanQR()}
              className="w-11 h-11 rounded-full flex items-center justify-center"
              style={{ background: '#0B3B30' }}
              data-testid="operacoes-qr-btn"
            >
              <QrCode className="h-5 w-5" style={{ color: '#FFFFFF' }} />
            </button>
          </div>
        </div>

        {/* Tabs: Operações / Pontuais */}
        <div className="flex border-b border-stone-200 -mx-5 px-5">
          <button
            onClick={() => setActiveTab('operacoes')}
            className={`flex-1 py-3 text-center text-sm font-semibold transition-all ${activeTab === 'operacoes' ? 'text-stone-900 border-b-2 border-stone-900' : 'text-stone-400'}`}
            data-testid="tab-operacoes"
          >
            Operações
          </button>
          <button
            onClick={() => setActiveTab('pontuais')}
            className={`flex-1 py-3 text-center text-sm font-semibold transition-all relative ${activeTab === 'pontuais' ? 'text-stone-900 border-b-2 border-stone-900' : 'text-stone-400'}`}
            data-testid="tab-pontuais"
          >
            Pontuais
            {tarefasPontuais.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold rounded-full bg-red-500 text-white min-w-[20px]">
                {tarefasPontuais.length}
              </span>
            )}
          </button>
        </div>

        {/* Search bar (only on Operações tab) */}
        {activeTab === 'operacoes' && (
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-xl mt-4"
            style={{ background: '#FFFFFF', border: '1px solid #E2E8F0' }}
          >
            <Search className="h-5 w-5 flex-shrink-0" style={{ color: '#94A3B8' }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Pesquisar por OF, cliente ou modelo..."
              className="flex-1 text-sm font-medium outline-none bg-transparent"
              style={{ color: '#1E293B' }}
              data-testid="operacoes-search"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')}>
                <X className="h-4 w-4" style={{ color: '#94A3B8' }} />
              </button>
            )}
          </div>
        )}
      </div>

      {activeTab === 'operacoes' && (
        <>
      {/* Filter pills — horizontal scroll conforme TSX */}
      <div
        className="overflow-x-auto scrollbar-hide"
        style={{ background: '#FFFFFF', borderBottom: '1px solid #E2E8F0' }}
      >
        <div className="flex gap-2 px-5 py-3" style={{ minWidth: 'max-content' }}>
          {filters.map(f => {
            const isActive = activeFilter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setActiveFilter(f.id)}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-2xl text-[13px] font-semibold transition-all whitespace-nowrap"
                style={{
                  background: isActive ? '#1E293B' : '#FFFFFF',
                  color: isActive ? '#FFFFFF' : '#64748B',
                  border: isActive ? '1px solid #1E293B' : '1px solid #E2E8F0',
                }}
                data-testid={`filter-${f.id}`}
              >
                {f.label}
                <span
                  className="text-[11px] font-bold px-1.5 py-0.5 rounded-lg"
                  style={{
                    background: isActive ? 'rgba(255,255,255,0.2)' : '#E2E8F0',
                    color: isActive ? '#FFFFFF' : '#64748B',
                  }}
                >
                  {f.count}
                </span>
              </button>
            );
          })}
          {/* Limpar Filtro — visível quando filtro activo */}
          {activeFilter !== 'todas' && (
            <button
              onClick={() => setActiveFilter('todas')}
              className="flex items-center gap-1.5 px-4 py-2 rounded-2xl text-[13px] font-bold whitespace-nowrap transition-all"
              style={{
                background: '#DC2626',
                color: '#FFFFFF',
                border: '2px solid #DC2626',
              }}
              data-testid="btn-limpar-filtro"
            >
              <X className="h-3.5 w-3.5" />
              Limpar Filtro
            </button>
          )}
        </div>
      </div>

      {/* Lista hierárquica de OFs */}
      <div className="flex-1 px-4 py-4 space-y-3 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-sm" style={{ color: '#94A3B8' }}>
              {searchQuery.length >= 2
                ? `Sem resultados para "${searchQuery}"`
                : 'Nenhuma OF disponível'}
            </p>
          </div>
        ) : (
          filtered.map(of => {
            const inProducao = of._status === 'em_producao';
            const terminada = of._status === 'terminada';
            const isUrgente = of.urgente;
            const temCongelada = operacoesCongeladas.some(c => c.of_id === of.id);
            const isExpanded = expandedOf === of.id;
            const lotes = (of.lotes || []).filter(l =>
              (l.status === 'aprovado' || !l.status) && (l.operacoes_operador || []).length > 0
            );
            if (lotes.length === 0) return null;
            const isEspecial = (of.categorias_especiais || []).length > 0;
            const headerBg = isEspecial ? '#4C1D95' : '#0B3B30';
            const accentColor = isEspecial ? '#7C3AED' : '#0B3B30';

            const allOps = lotes.flatMap(l => l.operacoes_operador || []);
            const donOps = allOps.filter(op => {
              const max = op.quantidade_total || 1;
              return (op.quantidade_produzida_todos ?? op.quantidade_produzida ?? 0) >= max;
            });
            const progressoGlobal = allOps.length > 0
              ? Math.round((donOps.length / allOps.length) * 100) : 0;
            const qtdLote = lotes.reduce((sum, l) => sum + (l.quantidade_ajustada || l.quantidade || 0), 0);

            // Status badge config
            let statusLabel = 'Nova';
            let statusBg = '#E0F2FE';
            let statusColor = '#2563EB';
            if (terminada) { statusLabel = 'Terminada'; statusBg = '#DCFCE7'; statusColor = '#10B981'; }
            else if (inProducao) { statusLabel = 'Em produção'; statusBg = '#DCFCE7'; statusColor = '#10B981'; }
            if (temCongelada) { statusLabel = 'Pausada'; statusBg = '#FEF3C7'; statusColor = '#F59E0B'; }

            return (
              <div
                key={of.id}
                className="rounded-2xl overflow-hidden"
                style={{
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  border: temCongelada ? '2px solid #3B82F6' : isUrgente ? '2px solid #EF4444' : 'none',
                }}
                data-testid={`operacoes-card-${of.numero_of}`}
              >
                {/* Parte verde/roxa — OF ID + badges + modelo/marca + chevron */}
                <div
                  className="px-4 pt-3 pb-2 cursor-pointer"
                  style={{ background: headerBg }}
                  onClick={() => toggleOf(of.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      {/* OF number — first line */}
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className="font-black text-xl text-white">
                          OF {of.numero_of}
                        </span>
                        <span
                          className="px-2 py-0.5 rounded-lg text-[11px] font-bold"
                          style={{ background: 'rgba(255,255,255,0.2)', color: '#FFFFFF' }}
                        >
                          {statusLabel}
                        </span>
                        {temCongelada && (
                          <span
                            className="px-2 py-0.5 rounded-lg text-[11px] font-bold flex items-center gap-1"
                            style={{ background: '#3B82F6', color: '#FFFFFF' }}
                          >
                            <Snowflake className="h-3 w-3" /> Congelada
                          </span>
                        )}
                        {isUrgente && (
                          <span
                            className="px-2 py-0.5 rounded-lg text-[11px] font-bold flex items-center gap-1"
                            style={{ background: '#EF4444', color: '#FFFFFF' }}
                          >
                            <Zap className="h-3 w-3" /> Urgente
                          </span>
                        )}
                        {isEspecial && (
                          <span
                            className="px-2 py-0.5 rounded-lg text-[11px] font-bold"
                            style={{ background: '#A78BFA', color: '#FFFFFF' }}
                          >
                            Especial
                          </span>
                        )}
                      </div>
                      {/* Modelo (GRANDE) + Marca (pequena) */}
                      <p className="text-xl font-extrabold text-white" style={{ opacity: 0.95 }}>
                        {of.artigo}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-xs text-white" style={{ opacity: 0.6 }}>
                          {of.cliente}
                        </p>
                        {allOps.length > 0 && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ 
                            background: donOps.length > 0 ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.15)', 
                            color: '#FFFFFF' 
                          }}>
                            {donOps.length}/{allOps.length} ops
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="h-6 w-6 text-white flex-shrink-0 ml-2" style={{ opacity: 0.6, transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                  </div>
                </div>

                {/* Parte branca — LOTES + QTD + progresso */}
                <div
                  className="px-4 py-3 cursor-pointer"
                  style={{ background: '#FFFFFF' }}
                  onClick={() => toggleOf(of.id)}
                >
                  <div className="flex items-end justify-between">
                    <div className="flex gap-6">
                      <div>
                        <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: '#64748B' }}>LOTES</p>
                        <p className="font-black text-2xl" style={{ color: '#1E293B' }}>{lotes.length}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: '#64748B' }}>QTD.</p>
                        <p className="font-black text-2xl" style={{ color: '#1E293B' }}>{qtdLote} <span className="text-sm font-semibold" style={{ color: '#64748B' }}>un.</span></p>
                      </div>
                    </div>
                    <p className="text-sm font-bold" style={{ color: '#64748B' }}>
                      {donOps.length} de {allOps.length} ops
                    </p>
                  </div>
                  {/* Progress bar */}
                  {allOps.length > 0 && (
                    <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: '#E2E8F0' }}>
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${progressoGlobal}%`, background: accentColor }}
                      />
                    </div>
                  )}
                </div>

                {/* Expanded: Lotes list */}
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-2">
                    <div className="h-px" style={{ background: '#E2E8F0' }} />
                    <p className="text-xs font-bold uppercase tracking-wider pt-1" style={{ color: '#64748B' }}>
                      LOTES ({lotes.length})
                    </p>

                    {lotes.map(lote => {
                      const loteCode = lote.nome_lote || lote.codigo_lote || `L${lote.numero_lote}` || '-';
                      const loteQty = lote.quantidade_ajustada || lote.quantidade || 0;
                      const isComplete = isLoteComplete(lote);
                      const progress = getLoteProgress(lote);
                      const isLoteExpanded = expandedLote === lote.id;
                      const ops = lote.operacoes_operador || [];
                      const categoriaNome = lote.categoria_nome || lote.categoria || '';

                      return (
                        <div key={lote.id} data-testid={`lote-card-${loteCode}`}>
                          {/* Lote row — larger for touch */}
                          <div
                            className={`flex items-center gap-3 px-4 py-4 rounded-xl cursor-pointer transition-all ${
                              isComplete ? 'opacity-60' : ''
                            }`}
                            style={{
                              background: isComplete ? '#F0FDF4' : isLoteExpanded ? '#F1F5F9' : '#FAFAFA',
                              border: isComplete ? '1px solid #D1FAE5' : '1px solid #E2E8F0',
                            }}
                            onClick={() => { if (!isComplete) toggleLote(lote.id); }}
                          >
                            {isComplete ? (
                              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#10B981' }}>
                                <Check className="h-5 w-5 text-white" />
                              </div>
                            ) : (
                              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: lote.categoria_cor || accentColor }}>
                                <Package className="h-5 w-5 text-white" />
                              </div>
                            )}

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-extrabold text-lg" style={{ color: '#1E293B' }}>
                                    {loteCode}
                                  </span>
                                  {categoriaNome && (
                                    <span className="text-xs px-2 py-0.5 rounded-lg font-bold" style={{ background: lote.categoria_cor || (isEspecial ? '#7C3AED' : '#0284C7'), color: '#FFFFFF' }}>
                                      {categoriaNome}
                                    </span>
                                  )}
                                  {isComplete && (
                                    <span className="text-[11px] px-2 py-0.5 rounded-lg font-bold" style={{ background: '#10B981', color: '#FFFFFF' }}>
                                      Concluído
                                    </span>
                                  )}
                                </div>
                                <span className="text-sm font-bold" style={{ color: '#64748B' }}>
                                  {loteQty} un.
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: '#E2E8F0' }}>
                                  <div
                                    className="h-full rounded-full"
                                    style={{
                                      width: `${progress.percent}%`,
                                      background: isComplete ? '#10B981' : '#F59E0B',
                                    }}
                                  />
                                </div>
                                <span className="text-xs font-bold flex-shrink-0" style={{ color: '#94A3B8' }}>
                                  {progress.done}/{progress.total}
                                </span>
                              </div>
                            </div>

                            {!isComplete && (
                              isLoteExpanded ? (
                                <ChevronDown className="h-5 w-5 flex-shrink-0" style={{ color: '#94A3B8' }} />
                              ) : (
                                <ChevronRight className="h-5 w-5 flex-shrink-0" style={{ color: '#94A3B8' }} />
                              )
                            )}
                          </div>

                          {/* Expanded: Operations list — sem DnD (só gestor pode ordenar) */}
                          {isLoteExpanded && !isComplete && ops.length > 0 && (
                              <div className="ml-4 mt-2 space-y-2">
                                    {ops.map((op, index) => {
                                      const max = op.quantidade_total || loteQty || 1;
                                      const produzido = op.quantidade_produzida_todos ?? op.quantidade_produzida ?? 0;
                                      const opDone = produzido >= max;
                                      const opInProgress = produzido > 0 && produzido < max;
                                      const opCongelada = operacoesCongeladas.some(c =>
                                        c.operacao_id === op.operacao_id && c.of_id === of.id
                                      );
                                      const isActiveOp = producaoAtiva?.registo?.operacao_id === op.operacao_id 
                                        && producaoAtiva?.registo?.lote_id === lote.id
                                        && producaoAtiva?.registo?.of_id === of.id;
                                      const opProgressPercent = max > 0 ? Math.round((produzido / max) * 100) : 0;

                                      return (
                                            <div
                                              key={`${op.operacao_id}-${lote.id}-${index}`}
                                              className={`rounded-xl overflow-hidden transition-all ${isActiveOp ? 'ring-2 ring-green-400 animate-pulse' : ''}`}
                                              style={{
                                                background: isActiveOp ? '#F0FDF4' : opDone ? '#F0FDF4' : '#FFFFFF',
                                                border: isActiveOp ? '2px solid #10B981'
                                                  : opDone ? '1px solid #D1FAE5'
                                                  : opCongelada ? '1px solid #93C5FD'
                                                  : opInProgress ? '1px solid #FDE68A'
                                                  : '1px solid #E2E8F0',
                                              }}
                                              data-testid={`op-item-${op.operacao_codigo || op.codigo}`}
                                            >
                                              {/* Op content — clickable para iniciar */}
                                              <div
                                                className={`px-3 py-3 flex items-center gap-3 ${(!opDone && !producaoAtiva?.registo?.em_pausa) ? 'cursor-pointer' : ''}`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  // Se operador em pausa — bloquear
                                                  if (producaoAtiva?.registo?.em_pausa) {
                                                    return; // Silencioso — o operador vê que está em pausa
                                                  }
                                                  if (opCongelada) {
                                                    const cong = operacoesCongeladas.find(c =>
                                                      c.operacao_id === op.operacao_id && c.of_id === of.id
                                                    );
                                                    if (cong && onResumeCongelada) onResumeCongelada(cong);
                                                  } else if (isActiveOp && onOpenActiveProduction) {
                                                    // Abrir produção ativa directamente
                                                    onOpenActiveProduction();
                                                  } else if (!opDone && !isActiveOp) {
                                                    // Iniciar operação — fluxo direto via StartOperationScreen
                                                    if (onStartOperation) {
                                                      onStartOperation({ of, op, lote });
                                                    }
                                                  }
                                                }}
                                              >

                                                {/* Status icon — larger */}
                                                {opDone ? (
                                                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#DCFCE7' }}>
                                                    <Check className="h-4 w-4" style={{ color: '#10B981' }} />
                                                  </div>
                                                ) : opCongelada ? (
                                                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#DBEAFE' }}>
                                                    <Play className="h-4 w-4" style={{ color: '#3B82F6' }} />
                                                  </div>
                                                ) : opInProgress ? (
                                                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#FEF3C7' }}>
                                                    <Clock className="h-4 w-4" style={{ color: '#F59E0B' }} />
                                                  </div>
                                                ) : (
                                                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#F1F5F9' }}>
                                                    <Play className="h-4 w-4" style={{ color: '#1E293B' }} />
                                                  </div>
                                                )}

                                                {/* Op info — larger text */}
                                                <div className="flex-1 min-w-0">
                                                  <div className="flex items-center gap-2">
                                                    <span
                                                      className="px-2 py-0.5 rounded text-xs font-bold"
                                                      style={{ background: '#F1F5F9', color: '#1E293B' }}
                                                    >
                                                      {op.operacao_codigo || op.codigo}
                                                    </span>
                                                    <span className="text-sm font-semibold truncate" style={{ color: '#1E293B' }}>
                                                      {op.operacao_nome || op.nome}
                                                    </span>
                                                  </div>
                                                  {/* Status badges */}
                                                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                                    {opDone && (
                                                      <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: '#10B981', color: '#FFFFFF' }}>
                                                        Concluído
                                                      </span>
                                                    )}
                                                    {isActiveOp && !opDone && (
                                                      <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: '#059669', color: '#FFFFFF' }}>
                                                        Em Curso
                                                      </span>
                                                    )}
                                                    {opCongelada && !opDone && !isActiveOp && (
                                                      <span className="text-[10px] px-1.5 py-0.5 rounded font-bold flex items-center gap-0.5" style={{ background: '#DBEAFE', color: '#1D4ED8' }}>
                                                        <Snowflake className="h-2.5 w-2.5" />
                                                        Congelada
                                                      </span>
                                                    )}
                                                    {opInProgress && !isActiveOp && !opCongelada && (
                                                      <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: '#FEF3C7', color: '#92400E' }}>
                                                        Parcial
                                                      </span>
                                                    )}
                                                    {((op.defeitos || 0) + (op.arranjos || 0)) > 0 && (
                                                      <span className="text-[10px] px-1.5 py-0.5 rounded font-bold flex items-center gap-0.5" style={{ background: '#FEF3C7', color: '#92400E' }}>
                                                        <AlertTriangle className="h-2.5 w-2.5" />
                                                        {(op.defeitos || 0) + (op.arranjos || 0)} anomalia(s)
                                                      </span>
                                                    )}
                                                    {(() => {
                                                      const desvio = (op.quantidade_total || loteQty || 0) - produzido - (op.defeitos || 0) - (op.arranjos || 0);
                                                      if (opDone && desvio > 0) {
                                                        return (
                                                          <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: '#FEE2E2', color: '#991B1B' }}>
                                                            Desvio: {desvio}
                                                          </span>
                                                        );
                                                      }
                                                      return null;
                                                    })()}
                                                  </div>
                                                  {/* Progress bar */}
                                                  <div className="flex items-center gap-2 mt-1.5">
                                                    <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: '#E2E8F0' }}>
                                                      <div
                                                        className="h-full rounded-full"
                                                        style={{
                                                          width: `${opProgressPercent}%`,
                                                          background: opDone ? '#10B981' : opInProgress ? '#F59E0B' : '#CBD5E1',
                                                        }}
                                                      />
                                                    </div>
                                                    <span className="text-xs font-bold flex-shrink-0" style={{ color: opDone ? '#10B981' : '#64748B' }}>
                                                      {produzido}/{max}
                                                    </span>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                      );
                                    })}
                              </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Action button removed — user navigates via lote click */}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
        </>
      )}

      {activeTab === 'pontuais' && (
        <div className="flex-1 overflow-y-auto">
          {/* Sub-tabs: Disponíveis / Histórico Dia */}
          <div className="flex gap-2 px-5 py-3" style={{ background: '#FFFFFF', borderBottom: '1px solid #E2E8F0' }}>
            <button
              onClick={() => setPontuaisSubTab('disponiveis')}
              className="flex-1 py-2.5 rounded-full text-sm font-bold transition-all"
              style={{
                background: pontuaisSubTab === 'disponiveis' ? '#8B5CF6' : '#F1F5F9',
                color: pontuaisSubTab === 'disponiveis' ? '#FFFFFF' : '#64748B',
              }}
              data-testid="subtab-disponiveis"
            >
              Disponíveis ({tarefasPontuais.length})
            </button>
            <button
              onClick={() => setPontuaisSubTab('historico')}
              className="flex-1 py-2.5 rounded-full text-sm font-bold transition-all"
              style={{
                background: pontuaisSubTab === 'historico' ? '#8B5CF6' : '#F1F5F9',
                color: pontuaisSubTab === 'historico' ? '#FFFFFF' : '#64748B',
              }}
              data-testid="subtab-historico"
            >
              Histórico ({tarefasHistoricoDia.length})
            </button>
          </div>

          <div className="p-4">
          {pontuaisSubTab === 'disponiveis' ? (
            <>
            {tarefasPontuais.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-stone-400">
                <Package className="h-12 w-12 mb-3 opacity-50" />
                <p className="text-sm font-medium">Sem tarefas pontuais</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tarefasPontuais.map((tarefa) => (
                  <div
                    key={tarefa.id}
                    className="bg-white rounded-2xl overflow-hidden"
                    style={{ border: '1px solid #E2E8F0' }}
                    data-testid={`tarefa-pontual-${tarefa.id}`}
                  >
                    <div className="flex">
                      {/* Borda lateral colorida */}
                      <div className="w-1.5 flex-shrink-0" style={{ background: tarefa.urgente ? '#EF4444' : '#8B5CF6' }} />
                      
                      <div className="flex-1 p-4">
                        {/* Título + Urgente */}
                        <div className="flex items-start justify-between mb-1.5">
                          <p className="text-base font-bold text-stone-800">{tarefa.nome || tarefa.titulo || tarefa.descricao || 'Tarefa pontual'}</p>
                          {tarefa.urgente && (
                            <span className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: '#FEE2E2', color: '#DC2626' }}>
                              <Zap className="h-3 w-3" /> Urgente
                            </span>
                          )}
                        </div>
                        
                        {/* Marca · Modelo */}
                        {(tarefa.marca || tarefa.modelo) && (
                          <p className="text-sm text-stone-500 mb-2">
                            {tarefa.marca || '—'} · {tarefa.modelo || '—'}
                          </p>
                        )}
                        
                        {/* Operações (lista completa) */}
                        {(tarefa.operacoes || []).length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {tarefa.operacoes.map((op, idx) => (
                              <span key={idx} className="text-xs px-2 py-0.5 rounded font-bold" style={{ background: '#EDE9FE', color: '#7C3AED' }}>
                                {op.operacao_codigo}{op.operacao_nome ? ` - ${op.operacao_nome}` : ''}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* Observações */}
                        {tarefa.observacoes && (
                          <p className="text-xs italic mb-2" style={{ color: '#64748B' }}>{tarefa.observacoes}</p>
                        )}
                        
                        {/* Botão Iniciar ou Descongelar */}
                        <div className="flex justify-end mt-2">
                          {frozenTarefaId === tarefa.id ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); onIniciarTarefa && onIniciarTarefa(tarefa); }}
                              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold transition-all active:scale-95"
                              style={{ border: '2px solid #3B82F6', color: '#3B82F6', background: '#EFF6FF' }}
                              data-testid={`btn-descongelar-tarefa-${tarefa.id}`}
                            >
                              <Snowflake className="h-4 w-4" /> Descongelar
                            </button>
                          ) : (
                            <button
                              onClick={(e) => { e.stopPropagation(); onIniciarTarefa && onIniciarTarefa(tarefa); }}
                              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold transition-all active:scale-95"
                              style={{ border: '2px solid #8B5CF6', color: '#8B5CF6', background: '#FAFAFF' }}
                              data-testid={`btn-iniciar-tarefa-${tarefa.id}`}
                            >
                              <Play className="h-4 w-4" /> Iniciar
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            </>
          ) : (
            <>
            {tarefasHistoricoDia.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-stone-400">
                <Clock className="h-12 w-12 mb-3 opacity-50" />
                <p className="text-sm font-medium">Sem tarefas concluídas</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tarefasHistoricoDia.map((tarefa) => {
                  const isContagem = !!(
                    tarefa.obriga_contagem ||
                    tarefa.is_contagem ||
                    (Array.isArray(tarefa.operacoes) && tarefa.operacoes.some(o => o?.is_contagem === true))
                  );
                  const fmtTime = (iso) => {
                    if (!iso) return null;
                    try {
                      return new Date(iso).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
                    } catch (_) { return null; }
                  };
                  const fmtDate = (iso) => {
                    if (!iso) return null;
                    try {
                      return new Date(iso).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: '2-digit' });
                    } catch (_) { return null; }
                  };
                  // Tempo efetivo (já desconta congelamento; tempo_segundos é autoritativo do backend)
                  let tempoSeg = null;
                  if (tarefa.tempo_segundos !== undefined && tarefa.tempo_segundos !== null) {
                    tempoSeg = Number(tarefa.tempo_segundos);
                  } else if (tarefa.tempo_minutos !== undefined && tarefa.tempo_minutos !== null) {
                    tempoSeg = Math.round(Number(tarefa.tempo_minutos) * 60);
                  } else if (tarefa.inicio_em && tarefa.concluido_em) {
                    try {
                      tempoSeg = Math.max(0, Math.floor((new Date(tarefa.concluido_em) - new Date(tarefa.inicio_em)) / 1000));
                      // Subtrair pausas se existirem (foi_congelada já está descontado em tempo_segundos do backend)
                      const pausa = Number(tarefa.total_pausa_segundos || 0);
                      if (pausa > 0) tempoSeg = Math.max(0, tempoSeg - pausa);
                    } catch (_) { tempoSeg = null; }
                  }
                  const fmtDur = (s) => {
                    if (s === null || s === undefined || isNaN(s)) return '—';
                    if (s < 60) return `${s}s`;
                    const h = Math.floor(s / 3600);
                    const m = Math.floor((s % 3600) / 60);
                    const r = s % 60;
                    if (h > 0) return r > 0 ? `${h}h ${m}m ${r}s` : `${h}h ${m}m`;
                    return r > 0 ? `${m}m ${r}s` : `${m}m`;
                  };
                  const horaInicio = fmtTime(tarefa.inicio_em);
                  const horaFim = fmtTime(tarefa.concluido_em);
                  const dataConclusao = fmtDate(tarefa.concluido_em || tarefa.inicio_em);
                  const tempoFmt = fmtDur(tempoSeg);
                  const foiPausada = !!(tarefa.foi_pausada || (tarefa.contador_pausas || 0) > 0 || (tarefa.total_pausa_segundos || 0) > 0);
                  const foiCongelada = !!(tarefa.foi_congelada || (tarefa.contador_congelacoes || 0) > 0);
                  return (
                  <div
                    key={tarefa.id}
                    className="bg-white rounded-2xl overflow-hidden"
                    style={{ border: '1px solid #D1FAE5' }}
                    data-testid={`tarefa-historico-${tarefa.id}`}
                  >
                    <div className="flex">
                      <div className="w-1.5 flex-shrink-0" style={{ background: '#10B981' }} />
                      <div className="flex-1 p-4">
                        {/* Header: Título + Badge */}
                        <div className="flex items-start justify-between mb-2">
                          <p className="text-base font-bold text-stone-800">
                            {tarefa.tarefa_nome || tarefa.nome || tarefa.titulo || tarefa.descricao || 'Tarefa pontual'}
                          </p>
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold flex-shrink-0" style={{ background: '#DCFCE7', color: '#166534' }}>
                            Concluída
                          </span>
                        </div>
                        
                        {/* Marca · Modelo */}
                        {(tarefa.marca || tarefa.modelo) && (
                          <p className="text-sm text-stone-500 mb-1.5">
                            {tarefa.marca || '—'} · {tarefa.modelo || '—'}
                          </p>
                        )}
                        
                        {/* Operações */}
                        {(tarefa.operacoes || []).length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {tarefa.operacoes.map((op, idx) => (
                              <span key={idx} className="text-xs px-2 py-0.5 rounded font-bold" style={{ background: '#EDE9FE', color: '#7C3AED' }}>
                                {op.operacao_codigo} - {op.operacao_nome}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* Observações */}
                        {tarefa.observacoes && (
                          <p className="text-xs italic mb-2" style={{ color: '#64748B' }}>
                            "{tarefa.observacoes}"
                          </p>
                        )}
                        
                        {/* Footer: Quantidade (se contagem) OU Data+Horas+Tempo (se não contagem) */}
                        <div className="flex items-center justify-between mt-1 pt-2 gap-2 flex-wrap" style={{ borderTop: '1px solid #F1F5F9' }}>
                          {isContagem ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold" style={{ color: '#64748B' }}>Quantidade:</span>
                              <span className="text-sm font-extrabold" style={{ color: '#10B981' }}>
                                {tarefa.quantidade ?? tarefa.quantidade_concluida ?? tarefa.quantidade_reportada ?? tarefa.quantidade_final ?? 0} peça(s)
                              </span>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-0.5 text-xs" data-testid={`tempo-info-${tarefa.id}`}>
                              <div className="flex items-center gap-2 flex-wrap" style={{ color: '#64748B' }}>
                                {dataConclusao && <span className="font-medium" style={{ color: '#475569' }}>{dataConclusao}</span>}
                                <span className="flex items-center gap-1">
                                  <span style={{ color: '#10B981' }}>●</span>
                                  <strong style={{ color: '#10B981' }}>{horaInicio || '—'}</strong>
                                  <span style={{ color: '#94A3B8' }}>→</span>
                                  <span style={{ color: '#EF4444' }}>●</span>
                                  <strong style={{ color: '#EF4444' }}>{horaFim || '—'}</strong>
                                </span>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-extrabold text-sm" style={{ color: '#2563EB' }}>{tempoFmt}</span>
                                <span className="text-[10px]" style={{ color: '#94A3B8' }}>(tempo efetivo, sem pausas/congelamento)</span>
                                {foiPausada && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: '#FEF3C7', color: '#B45309' }}>
                                    Pausada{tarefa.contador_pausas ? ` (${tarefa.contador_pausas}x)` : ''}
                                  </span>
                                )}
                                {foiCongelada && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: '#CFFAFE', color: '#0E7490' }}>
                                    Congelada{tarefa.contador_congelacoes ? ` (${tarefa.contador_congelacoes}x)` : ''}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                          {onEditarTarefaConcluida && isContagem && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onEditarTarefaConcluida(tarefa); }}
                              className="text-xs font-bold px-3 py-1 rounded-lg transition-all active:scale-95"
                              style={{ background: '#F1F5F9', color: '#64748B' }}
                              data-testid={`btn-editar-tarefa-${tarefa.id}`}
                            >
                              Editar
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
            </>
          )}
          </div>
        </div>
      )}
    </div>
  );
}
